import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../utils/cn/cn';
import { Chip, type ChipProps } from '../Chip/Chip';

const Breadcrumbs = React.forwardRef<HTMLElement, React.ComponentPropsWithoutRef<'nav'>>(
  ({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />,
);
Breadcrumbs.displayName = 'Breadcrumbs';

const BreadcrumbsList = React.forwardRef<HTMLOListElement, React.ComponentPropsWithoutRef<'ol'>>(
  ({ className, ...props }, ref) => (
    <ol ref={ref} className={cn('flex flex-wrap items-center gap-1', className)} {...props} />
  ),
);
BreadcrumbsList.displayName = 'BreadcrumbsList';

const BreadcrumbsItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn(
        'text-size-body-sm text-color-text-darktransparent last:text-color-text-dark inline-flex items-center last:font-bold',
        className,
      )}
      {...props}
    />
  ),
);
BreadcrumbsItem.displayName = 'BreadcrumbsItem';

const BreadcrumbsLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'> & {
    asChild?: boolean;
  }
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp ref={ref} className={cn('text-size-body-sm hover:underline', className)} {...props} />
  );
});
BreadcrumbsLink.displayName = 'BreadcrumbsLink';

const BreadcrumbsPage = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<'h1' | 'span'> & { lastItemAs?: 'span' | 'h1' }
>(({ className, children, lastItemAs = 'span', ...props }, ref) => {
  const Comp = lastItemAs;

  return (
    <Comp ref={ref} aria-current="page" className={cn('text-size-body-sm', className)} {...props}>
      {children}
    </Comp>
  );
});
BreadcrumbsPage.displayName = 'BreadcrumbsPage';

const BreadcrumbsSeparator = ({ children, className, ...props }: React.ComponentProps<'li'>) => (
  <li role="presentation" aria-hidden="true" {...props} className={className}>
    {children ?? '/'}
  </li>
);
BreadcrumbsSeparator.displayName = 'BreadcrumbsSeparator';

const BreadcrumbsEllipsis = ({ ...props }: Omit<ChipProps, 'label' | 'className'>) => (
  <Chip {...props} className="m-0 h-2.5" size="small" label="..." variant="filled" />
);

BreadcrumbsEllipsis.displayName = 'BreadcrumbElipssis';

export {
  Breadcrumbs,
  BreadcrumbsList,
  BreadcrumbsItem,
  BreadcrumbsLink,
  BreadcrumbsPage,
  BreadcrumbsSeparator,
  BreadcrumbsEllipsis,
};
